import CircularProgress from '@mui/material/CircularProgress';
import { useAuthStore } from 'features/auth/stores/authStore';
import { Users, User } from 'lucide-react';
import React, { useState } from 'react';
import { DateRangePicker } from 'shared/components/DateRangePicker';
import { HeroContainer } from 'shared/components/HeroContainer';

import { getDefaultStartDate, getDefaultEndDate } from '../../reports/utils/reportsUtils';
import {
  DashboardContainer,
  DashboardHeroActions,
  DashboardHeroRow,
  DashboardHeroStatCards,
  DashboardHeroSubtitle,
  DashboardHeroTextBox,
  DashboardHeroTitle,
  DashboardScopeButton,
  DashboardScopeToggle,
  LoadingBox,
} from '../dashboard.styles';
import { useDashboardSummary } from '../hooks/useDashboardSummary';
import { useDashboardTeamActivity } from '../hooks/useDashboardTeamActivity';
import { billabilityPercent } from '../utils/dashboardUtils';

import DashboardBarChart from './DashboardBarChart';
import DashboardDonutChart from './DashboardDonutChart';
import DashboardStatsBar from './DashboardStatsBar';
import DashboardTeamActivity from './DashboardTeamActivity';

type ScopeMode = 'team' | 'me';

const DashboardPage: React.FC = () => {
  const [startDate, setStartDate] = useState(getDefaultStartDate());
  const [endDate, setEndDate] = useState(getDefaultEndDate());
  const [scope, setScope] = useState<ScopeMode>('team');

  const currentUser = useAuthStore((s) => s.user);
  const userId = scope === 'me' ? currentUser?.id : undefined;

  const { data: summary, isLoading: summaryLoading } = useDashboardSummary(
    startDate,
    endDate,
    userId,
  );
  const { data: teamData, isLoading: teamLoading } = useDashboardTeamActivity(
    startDate,
    endDate,
    userId,
  );

  const isLoading = summaryLoading || teamLoading;
  const billPct = billabilityPercent(
    summary?.totalDurationSecs ?? 0,
    summary?.billableDurationSecs ?? 0,
  );
  const nonBillSecs = (summary?.totalDurationSecs ?? 0) - (summary?.billableDurationSecs ?? 0);

  const topProjectWithSecs = summary?.topProject
    ? {
        ...summary.topProject,
        totalSecs:
          summary.topProject.totalSecs ??
          summary.byProject?.find((p) => p.projectId === summary.topProject?.id)?.totalSecs,
      }
    : undefined;

  return (
    <DashboardContainer>
      {/* Hero banner */}
      <HeroContainer padding="24px 28px">
        <DashboardHeroRow>
          <DashboardHeroTextBox>
            <DashboardHeroTitle>Dashboard</DashboardHeroTitle>
            <DashboardHeroSubtitle>
              Track team productivity, billable hours, and project activity.
            </DashboardHeroSubtitle>
          </DashboardHeroTextBox>

          <DashboardHeroActions>
            <DashboardScopeToggle>
              <DashboardScopeButton
                active={scope === 'team'}
                onClick={() => setScope('team')}
                disableRipple
                startIcon={<Users size={14} />}
              >
                Team
              </DashboardScopeButton>
              <DashboardScopeButton
                active={scope === 'me'}
                onClick={() => setScope('me')}
                disableRipple
                startIcon={<User size={14} />}
              >
                Individual
              </DashboardScopeButton>
            </DashboardScopeToggle>

            <DateRangePicker
              startDate={startDate}
              endDate={endDate}
              onChange={(start, end) => {
                setStartDate(start);
                setEndDate(end);
              }}
              heromode
            />
          </DashboardHeroActions>
        </DashboardHeroRow>

        {/* Hero stat cards */}
        <DashboardHeroStatCards>
          <DashboardStatsBar
            totalSecs={summary?.totalDurationSecs ?? 0}
            billableSecs={summary?.billableDurationSecs ?? 0}
            billPct={billPct}
            nonBillSecs={nonBillSecs}
            entryCount={summary?.entryCount}
            topProject={topProjectWithSecs}
            topClient={summary?.topClient}
          />
        </DashboardHeroStatCards>
      </HeroContainer>

      {isLoading && (
        <LoadingBox>
          <CircularProgress size={32} />
        </LoadingBox>
      )}

      {!isLoading && (
        <>
          <DashboardBarChart
            from={startDate}
            to={endDate}
            byDate={summary?.byDate ?? []}
            byProject={summary?.byProject ?? []}
          />

          <DashboardDonutChart
            projects={summary?.byProject ?? []}
            totalSecs={summary?.totalDurationSecs ?? 0}
          />

          <DashboardTeamActivity members={teamData?.members ?? []} from={startDate} to={endDate} />
        </>
      )}
    </DashboardContainer>
  );
};

export default DashboardPage;
